/** Add / edit / duplicate an event (staff). Mirrors the prototype's editor. */
import { api } from "../api";
import { S, on, render } from "../state";
import { $, closeSheet, edHead, esc, openSheet, toast } from "../ui";
import { EVENT_TYPES, nextWorkday, typeInfo } from "../../shared/constants";
import type { EventView, TemplateView } from "../../shared/types";
import { E, ensureStaffLists, put } from "../events";
import { C, openEvent } from "./calendar";
import { resetManage } from "./manage";

const ED: { id: number | null; back: number | null; endTouched: boolean; dur: number; type: string; status: string } = {
  id: null, back: null, endTouched: false, dur: 60, type: "", status: "draft",
};

export const PUB_MSG = (n: boolean) =>
  n ? "Published. Matching ambassadors get one notification 10 minutes after your last Publish & Notify." : "Published. No notification sent.";

export const byName = <T>(list: T[], f: (x: T) => string) => [...list].sort((a, b) => f(a).localeCompare(f(b), "en", { sensitivity: "base" }));

/* time picker: hour, 5-minute, AM/PM */
const toMin = (t: string) => {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
};
const fromMin = (n: number) => {
  n = Math.max(0, Math.min(n, 23 * 60 + 55));
  return `${String(Math.floor(n / 60)).padStart(2, "0")}:${String(n % 60).padStart(2, "0")}`;
};
export function tpick(p: string, t: string, change: string) {
  const [h, m] = t.split(":").map(Number);
  const h12 = h % 12 || 12;
  const opt = (v: string | number, l: string | number, sel: boolean) => `<option value="${v}" ${sel ? "selected" : ""}>${l}</option>`;
  return `<div class="row" style="gap:4px"><select class="f" id="${p}h" aria-label="Hour" data-change="${change}">${[12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((x) => opt(x, x, x === h12)).join("")}</select><select class="f" id="${p}m" aria-label="Minutes" data-change="${change}">${Array.from({ length: 12 }, (_, i) => i * 5).map((x) => opt(x, String(x).padStart(2, "0"), x === m - (m % 5))).join("")}</select><select class="f" id="${p}a" aria-label="AM or PM" data-change="${change}">${opt("AM", "AM", h < 12)}${opt("PM", "PM", h >= 12)}</select></div>`;
}
const sel = (id: string) => $(`#${id}`) as HTMLSelectElement;
export function tget(p: string) {
  let h = Number(sel(p + "h").value) % 12;
  if (sel(p + "a").value === "PM") h += 12;
  return fromMin(h * 60 + Number(sel(p + "m").value));
}
function tset(p: string, t: string) {
  const [h, m] = t.split(":").map(Number);
  sel(p + "h").value = String(h % 12 || 12);
  sel(p + "m").value = String(m - (m % 5));
  sel(p + "a").value = h >= 12 ? "PM" : "AM";
}

export function typeSel(id: string, cur: string, change: string) {
  const types = byName([...EVENT_TYPES], (t) => t.label);
  return `<div class="typesel"><span class="tdot" id="${id}dot" style="background:${cur ? typeInfo(cur).color : "transparent"};${cur ? "" : "border:2px dashed var(--line)"}"></span><select class="f" id="${id}" data-change="${change}">${cur ? "" : '<option value="" selected>Choose a type</option>'}${types.map((t) => `<option value="${t.key}" ${t.key === cur ? "selected" : ""}>${t.label}</option>`).join("")}</select></div>`;
}
export function setDot(id: string, k: string) {
  const d = $(`#${id}dot`)!;
  d.style.background = k ? typeInfo(k).color : "transparent";
  d.style.border = k ? "0" : "2px dashed var(--line)";
  $(`#${id}`)!.querySelector('option[value=""]')?.remove();
}

export function locSel(id: string, cur: number | null, curName: string | null) {
  const list = (E.locations || []).filter((l) => !l.deleted);
  // An event can keep a location that was removed later; it just isn't offered for new events.
  const keep = cur && !list.some((l) => l.id === cur) ? `<option value="${cur}" selected>${esc(curName || "Removed location")}</option>` : "";
  return `<select class="f" id="${id}"><option value="" ${!cur ? "selected" : ""}>No location</option>${keep}${byName(list, (l) => l.name).map((l) => `<option value="${l.id}" ${l.id === cur ? "selected" : ""}>${esc(l.name)}</option>`).join("")}</select>`;
}

type Pos = { pa: number; sa: number; paShadow: boolean; saShadow: boolean };
export const posSel = (id: string, v: number, lab: string) =>
  `<div><label class="fld" for="${id}">${lab}</label><select class="f" id="${id}">${Array.from({ length: 21 }, (_, i) => `<option ${i === v ? "selected" : ""}>${i}</option>`).join("")}</select></div>`;
export const shChk = (id: string, on: boolean, lab: string) =>
  `<label class="row" style="gap:8px;align-items:center;cursor:pointer;margin-top:8px"><input type="checkbox" id="${id}" ${on ? "checked" : ""} style="width:18px;height:18px">${lab}</label>`;
function posBlock(p: Pos, type: string) {
  if (type === "team") return '<p class="muted small" style="margin:0">Team Event: everyone sees it, no sign-up spots.</p>';
  return `<div class="grid" style="grid-template-columns:1fr 1fr;gap:10px"><div>${posSel("np", p.pa, "Parent Ambassadors")}${shChk("npsh", p.paShadow, "Allow PAs to Shadow")}</div><div>${posSel("nsa", p.sa, "Student Ambassadors")}${shChk("nssh", p.saShadow, "Allow SAs to Shadow")}</div></div><p class="muted small" style="margin:8px 0 0">Any number of ambassadors can add themselves as a shadow when it's allowed.</p>`;
}
function curPos(): Pos {
  if (!$("#np")) return { pa: 1, sa: 0, paShadow: false, saShadow: false };
  return { pa: Number(sel("np").value), sa: Number(sel("nsa").value), paShadow: ($("#npsh") as HTMLInputElement).checked, saShadow: ($("#nssh") as HTMLInputElement).checked };
}

function startChanged() {
  if (!ED.endTouched) tset("et", fromMin(toMin(tget("st")) + ED.dur));
  return checkEnd();
}
function checkEnd() {
  const bad = toMin(tget("et")) < toMin(tget("st"));
  $("#endmsg")!.textContent = bad ? "End must be at or after the start" : "";
  return !bad;
}
const counter = () => ($("#ntc")!.textContent = `${($("#nt") as HTMLInputElement).value.length}/30`);

/**
 * id null = new event. dup = copy of id. fromPop = Cancel goes back to that event's pop-up.
 * day = the date to start on (from a day's + on the calendar); otherwise the next school day.
 */
export async function openEditor(id: number | null, dup = false, fromPop = false, day?: string) {
  try {
    await ensureStaffLists();
  } catch (e) {
    return toast((e as Error).message);
  }
  const src = id ? E.byId.get(id) : undefined;
  const editing = !!src && !dup;
  const today = S.me!.today;
  const e = src
    ? { ...src }
    : { title: "", date: day || nextWorkday(today), start: "09:00", end: "10:00", locationId: null, locationName: null, pa: 1, sa: 0, paShadow: false, saShadow: false, type: "", notes: "", status: "draft" as const, signups: [] };
  Object.assign(ED, {
    id: editing ? id : null,
    back: fromPop ? id : null,
    endTouched: !!src,
    dur: src ? toMin(src.end) - toMin(src.start) : 60,
    type: src ? src.type : "",
    status: editing ? src!.status : "draft",
  });
  const live = editing && ED.status !== "draft";
  const tpls = byName(E.templates || [], (t) => t.name);
  const going = src ? src.signups.length : 0;
  openSheet(`${edHead(editing ? "Edit Event" : dup ? "Duplicate Event" : "Add Event")}
  <div class="edbody">
    ${editing ? "" : `<div class="edtpl"><label class="fld" for="tpl">Start From a Template</label><select class="f" id="tpl" data-change="ed-tpl"><option value="" selected>Blank</option>${tpls.map((t) => `<option value="${t.id}">${esc(t.name)}</option>`).join("")}</select></div>`}
    <section class="edsec"><h3>Event</h3>
      <div><label class="fld" for="nt">Name <span class="muted small" id="ntc"></span></label><input type="text" id="nt" maxlength="30" value="${esc(e.title)}" data-input="ed-count"></div>
      <div><label class="fld" for="ntype">Type &amp; Color</label>${typeSel("ntype", ED.type, "ed-type")}</div></section>
    <section class="edsec"><h3>When &amp; Where</h3>
      <div class="grid g2"><div><label class="fld" for="nd">Date</label><input type="date" id="nd" value="${dup ? "" : e.date}"></div><div><label class="fld" for="nl">Location</label>${locSel("nl", e.locationId, e.locationName)}</div></div>
      <div class="grid g2"><div><label class="fld">Start</label>${tpick("st", e.start, "ed-start")}</div><div><label class="fld">End</label>${tpick("et", e.end, "ed-end")}<div class="small" id="endmsg" style="color:var(--accent);margin-top:4px"></div></div></div></section>
    <section class="edsec"><h3>Ambassadors Needed</h3><div id="posblk">${posBlock(e, ED.type)}</div></section>
    <section class="edsec"><h3>Notes</h3>
      <div><label class="fld" for="nn">Notes for Ambassadors (Sent in Calendar Invite)</label><textarea id="nn" maxlength="2000" placeholder="e.g. JK4 Assessment">${esc(e.notes || "")}</textarea></div></section>
    ${live && going ? `<p class="muted small" style="margin:0">Changing the date, time or notes sends ${going} signed-up ambassador${going > 1 ? "s" : ""} an updated calendar invite.</p>` : ""}
    <div class="err" id="err" role="alert"></div>
  </div>
  <div class="edfoot">${
    live
      ? `<button class="btn" data-act="ed-save">Save Changes</button>`
      : `<button class="btn" data-act="ed-save" data-st="published" data-notify="1">Publish &amp; Notify</button><button class="btn ghost" data-act="ed-save" data-st="published">Publish</button><button class="btn ghost" data-act="ed-save" data-st="draft">Save as Draft</button>`
  }<button class="btn ghost" data-act="ed-back">Cancel</button></div>`);
  counter();
  if (dup) toast("Pick a date for the copy");
}

function applyTpl(t: TemplateView) {
  ($("#nt") as HTMLInputElement).value = t.name.slice(0, 30);
  counter();
  if (t.locationId && sel("nl").querySelector(`option[value="${t.locationId}"]`)) sel("nl").value = String(t.locationId);
  tset("st", t.start);
  ED.dur = t.duration;
  ED.endTouched = false;
  ED.type = t.type;
  startChanged();
  sel("ntype").value = t.type;
  setDot("ntype", t.type);
  $("#posblk")!.innerHTML = posBlock(t, t.type);
}

async function save(st: string | undefined, notify: boolean) {
  const err = (m: string) => {
    $("#err")!.textContent = m;
    toast(m);
  };
  const title = ($("#nt") as HTMLInputElement).value.trim();
  const date = ($("#nd") as HTMLInputElement).value;
  if (!title) return err("Give the event a name.");
  if (!date) return err("Pick a date.");
  if (!ED.type) return err("Choose a type.");
  if (!checkEnd()) return err("End must be at or after the start.");
  const team = ED.type === "team";
  const pos = team ? { pa: 0, sa: 0, paShadow: false, saShadow: false } : curPos();
  const body = { title, date, start: tget("st"), end: tget("et"), type: ED.type, locationId: sel("nl").value ? Number(sel("nl").value) : null, notes: ($("#nn") as HTMLTextAreaElement).value.trim(), ...pos };
  try {
    let ev: EventView;
    let msg: string;
    if (ED.id) {
      ev = put(await api<EventView>(`/api/events/${ED.id}`, { method: "PATCH", body }));
      if (st === "published" && ev.status === "draft") {
        ev = put(await api<EventView>(`/api/events/${ED.id}/publish`, { body: { notify } }));
        msg = PUB_MSG(notify);
      } else msg = st === "draft" ? "Saved as draft. Only Admissions can see it." : "Saved";
    } else {
      ev = put(await api<EventView>("/api/events", { body: { ...body, publish: st === "published", notify } }));
      msg = st === "published" ? PUB_MSG(notify) : "Saved as draft. Only Admissions can see it.";
    }
    const back = ED.back;
    resetManage();
    closeSheet();
    C.year = Number(date.slice(0, 4));
    C.month = Number(date.slice(5, 7)) - 1;
    C.sel = date;
    render();
    if (back) openEvent(ev.id);
    toast(msg);
  } catch (e) {
    err((e as Error).message);
  }
}

on({
  "ed-count": () => counter(),
  "ed-type": () => {
    const k = sel("ntype").value;
    ED.type = k;
    setDot("ntype", k);
    // New events pick up that type's usual length and spots from its first template; edits keep what's there.
    let pos = curPos();
    const t = !ED.id && byName(E.templates || [], (x) => x.name).find((x) => x.type === k);
    if (t) {
      pos = t;
      ED.dur = t.duration;
      if (!ED.endTouched) startChanged();
    }
    $("#posblk")!.innerHTML = posBlock(pos, k);
  },
  "ed-tpl": () => {
    const t = E.templates?.find((x) => x.id === Number(sel("tpl").value));
    if (t) applyTpl(t);
  },
  "ed-start": () => startChanged(),
  "ed-end": () => {
    ED.endTouched = true;
    checkEnd();
  },
  "ed-save": (el) => save(el.dataset.st, el.dataset.notify === "1"),
  "ed-back": () => (ED.back ? openEvent(ED.back) : closeSheet()),
});
